import React, { useCallback, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SearchIcon from "@mui/icons-material/Search";
import { useAuth } from "../../common/auth";
import { C } from "../../common/theme";
import {
  AcronymGroup,
  SearchPreview,
  deleteAcronymGroup,
  listAcronymGroups,
  previewSearch,
  saveAcronymGroup,
} from "../../common/requests/searchAcronyms/endpoints";
import { Banner, Column, ConfirmDialog, DataTable, EmptyState, PageShell, Panel, errorMessage, useIsNarrow, useResource, useToast } from "../../ui";

function parseTerms(value: string): string[] {
  const terms = value
    .split(",")
    .map((term) => term.trim().toLowerCase().replace(/\s+/g, " "))
    .filter(Boolean);
  return Array.from(new Set(terms));
}

function groupIdFor(terms: string[], existingIds: Set<string>): string {
  const base = (terms[0] || "group").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 56) || "group";
  let id = base;
  for (let n = 2; existingIds.has(id); n += 1) id = `${base}-${n}`;
  return id;
}

const acronymChipSx = { fontWeight: 700, textTransform: "uppercase" } as const;

function Terms({ terms }: { terms: string[] }) {
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
      {terms.map((term, index) => (
        <Chip
          key={term}
          size="small"
          label={term}
          color={index === 0 ? "primary" : "default"}
          variant={index === 0 ? "filled" : "outlined"}
          sx={index === 0 ? acronymChipSx : undefined}
        />
      ))}
    </Box>
  );
}

export default function SearchAcronyms() {
  const { idToken } = useAuth();
  const toast = useToast();
  const narrow = useIsNarrow(600);
  const groupsRes = useResource(async () => (await listAcronymGroups(idToken as string)).data, [idToken], {
    enabled: !!idToken,
    errorText: "Failed to load search acronyms.",
  });
  const groups = useMemo(() => groupsRes.data ?? [], [groupsRes.data]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [termsInput, setTermsInput] = useState("");
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<AcronymGroup | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const [previewQuery, setPreviewQuery] = useState("");
  const [preview, setPreview] = useState<SearchPreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const parsedTerms = useMemo(() => parseTerms(termsInput), [termsInput]);

  const runPreview = useCallback(
    async (query: string) => {
      if (!idToken || !query.trim()) return;
      setPreviewing(true);
      try {
        const res = await previewSearch(query.trim(), idToken);
        setPreview(res.data);
        setPreviewError(null);
      } catch (err: any) {
        setPreviewError(errorMessage(err, "Search preview failed."));
      } finally {
        setPreviewing(false);
      }
    },
    [idToken]
  );

  const openCreate = () => {
    setEditingId(null);
    setTermsInput("");
    setDialogError(null);
    setDialogOpen(true);
  };

  const openEdit = (group: AcronymGroup) => {
    setEditingId(group.id);
    setTermsInput(group.terms.join(", "));
    setDialogError(null);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!idToken) return;
    if (parsedTerms.length < 2) {
      setDialogError("Add at least two terms, e.g. an acronym and its full name.");
      return;
    }
    const id = editingId ?? groupIdFor(parsedTerms, new Set(groups.map((group) => group.id)));
    setSaving(true);
    try {
      await saveAcronymGroup(id, parsedTerms, idToken);
      setDialogOpen(false);
      toast(`Saved. Searches for "${parsedTerms[0]}" now match all ${parsedTerms.length} terms.`);
      await groupsRes.reload();
      setPreviewQuery(parsedTerms[0]);
      runPreview(parsedTerms[0]);
    } catch (err: any) {
      setDialogError(errorMessage(err, "Failed to save the group."));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!idToken || !deleteTarget) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteAcronymGroup(deleteTarget.id, idToken);
      toast(`Removed "${deleteTarget.terms[0]}".`);
      setDeleteTarget(null);
      await groupsRes.reload();
    } catch (err: any) {
      setDeleteError(errorMessage(err, "Failed to delete the group."));
    } finally {
      setDeleting(false);
    }
  };

  const columns: Column<AcronymGroup>[] = [
    { key: "terms", header: "Terms", card: "title", render: (g) => <Terms terms={g.terms} /> },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      width: 120,
      card: "aside",
      render: (g) => (
        <Box sx={{ display: "flex", justifyContent: "flex-end", whiteSpace: "nowrap" }}>
          <Tooltip title="Edit">
            <IconButton aria-label={`Edit ${g.terms[0]}`} onClick={() => openEdit(g)} sx={{ width: 44, height: 44 }}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              aria-label={`Delete ${g.terms[0]}`}
              onClick={() => {
                setDeleteError(null);
                setDeleteTarget(g);
              }}
              sx={{ width: 44, height: 44 }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <PageShell
      title="Search acronyms"
      subtitle="Every term in a group finds listings written with any other term. Changes apply to marketplace search immediately."
      actions={
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={{ minHeight: 44 }}>
          Add group
        </Button>
      }
    >
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "minmax(0, 3fr) minmax(0, 2fr)" }, gap: 2, alignItems: "start" }}>
        <DataTable
          aria-label="Acronym groups"
          rows={groups}
          columns={columns}
          getRowId={(g) => g.id}
          loading={groupsRes.status === "loading" || groupsRes.status === "idle"}
          error={groupsRes.error}
          onRetry={groupsRes.reload}
          empty={<EmptyState title="No acronym groups yet." action={<Button variant="outlined" onClick={openCreate}>Add group</Button>} />}
          dense
        />

        <Panel sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
          <Box>
            <Typography variant="h6" component="h2">
              Test a search
            </Typography>
            <Typography variant="caption" sx={{ color: C.muted }}>
              First page of live results, in the order buyers see them. Pure plants rank above hybrids.
            </Typography>
          </Box>
          <Box
            component="form"
            sx={{ display: "flex", gap: 1 }}
            onSubmit={(event: React.FormEvent) => {
              event.preventDefault();
              runPreview(previewQuery);
            }}
          >
            <TextField
              size="small"
              fullWidth
              placeholder="e.g. red vein dark phoenix"
              value={previewQuery}
              onChange={(event) => setPreviewQuery(event.target.value)}
              slotProps={{ htmlInput: { "aria-label": "Search query to preview", enterKeyHint: "search" } }}
            />
            <Button
              type="submit"
              variant="outlined"
              disabled={previewing || !previewQuery.trim()}
              startIcon={<SearchIcon fontSize="small" />}
              sx={{ minHeight: 40, flexShrink: 0 }}
            >
              {previewing ? "Searching…" : "Search"}
            </Button>
          </Box>
          {groupsRes.error && groups.length > 0 && <Banner tone="danger">{groupsRes.error}</Banner>}
          {previewError && <Banner tone="danger">{previewError}</Banner>}
          {preview && (
            <>
              <Typography variant="body2" sx={{ color: C.ink2 }}>
                {preview.total} result{preview.total === 1 ? "" : "s"}
                {preview.total > preview.results.length ? ` (showing first ${preview.results.length})` : ""}
              </Typography>
              <Box component="ol" sx={{ m: 0, pl: 3, display: "grid", gap: 0.5 }}>
                {preview.results.map((item) => (
                  <li key={item.listing_id}>
                    <Typography variant="body2">{item.name}</Typography>
                  </li>
                ))}
              </Box>
            </>
          )}
        </Panel>
      </Box>

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} fullWidth maxWidth="sm" fullScreen={narrow}>
        <DialogTitle>{editingId ? "Edit group" : "Add group"}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5, color: C.ink2 }}>
            Comma-separated, acronym first: <b>rvdp, red vein dark phoenix</b>. Leave out the cross marker "x" and the genus. Misspellings like
            "pheonix" are already handled.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            label="Terms"
            value={termsInput}
            onChange={(event) => setTermsInput(event.target.value)}
            error={!!dialogError}
            helperText={dialogError || " "}
            sx={{ mt: 0.5 }}
          />
          <Box sx={{ mt: 1, minHeight: 32 }}>
            <Terms terms={parsedTerms} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="outlined" onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || parsedTerms.length < 2}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        tone="danger"
        title={`Delete "${deleteTarget?.terms[0] ?? ""}"?`}
        body={`Searches for ${deleteTarget?.terms.map((term) => `"${term}"`).join(", ") ?? ""} will no longer find each other.`}
        confirmLabel="Delete"
        busy={deleting}
        error={deleteError}
        onConfirm={handleDelete}
        onClose={() => setDeleteTarget(null)}
      />
    </PageShell>
  );
}
